/**
 * Legacy color primitives.
 *
 * @deprecated Use `semanticColors` from `./tokens` for component styling.
 * These are kept for backward compatibility and extracted here so both
 * `theme.ts` and `tokens.ts` can reference them without circular deps.
 */

export type ColorPrimitive =
  | 'primary30'
  | 'primary100'
  | 'primary400'
  | 'primary600'
  | 'secondary100'
  | 'secondary400'
  | 'secondary600'
  | 'neutral100'
  | 'neutral300'
  | 'neutral400'
  | 'neutral500'
  | 'neutral600'
  | 'info30'
  | 'info100'
  | 'info400'
  | 'warning30'
  | 'warning100'
  | 'warning400'
  | 'success30'
  | 'success100'
  | 'success400'
  | 'alert30'
  | 'alert100'
  | 'alert400'
  | 'white'
  | 'gray60'
  | 'gray70'
  | 'gray80'
  | 'gray90'
  | 'gray100'
  | 'gray200'
  | 'gray300'
  | 'gray400'
  | 'gray500'
  | 'gray600'
  | 'gray700'
  | 'gray800'
  | 'gray900'
  | 'black'
  | 'heart'
  | 'primarySurface'
  | 'purple100'
  | 'purple400'
  | 'purple600'
  | 'goodstack'
  | 'text_primary'
  | 'contentTertiary'
  | 'contentDisabled'

/**
 * @deprecated Use `semanticColors` from `./tokens` instead.
 * These primitives are kept for backward compatibility and will be removed
 * once all components have migrated to semantic tokens.
 */
export const colorPrimitives: Record<ColorPrimitive, string> = {
  primary30: '#FBF8FF',
  /** @deprecated Use `semanticColors.backgroundErrorSubtle` */
  primary100: '#fff0f1',
  /** @deprecated Use `semanticColors.interactiveDefault` */
  primary400: '#ff6672',
  /** @deprecated Use `semanticColors.interactiveHover` */
  primary600: '#f34f5c',
  /** @deprecated Use `semanticColors.backgroundSuccessSubtle` */
  secondary100: '#e9fbf9',
  secondary400: '#23d3c1',
  secondary600: '#23c9b8',
  neutral100: '#e5effc',
  /** @deprecated Use `semanticColors.contentTertiary` */
  neutral300: '#6D6A73',
  /** @deprecated Use `semanticColors.contentPrimary` */
  neutral400: '#1b034f',
  neutral500: '#56525c',
  neutral600: '#110231',
  info30: '#f7f9ff',
  info100: '#e7f0ff',
  /** @deprecated Use `semanticColors.contentInfo` */
  info400: '#106cfe',
  /** @deprecated Use `semanticColors.backgroundWarningSubtle` */
  warning30: '#fefbf8',
  /** @deprecated Use `semanticColors.backgroundWarning` */
  warning100: '#fdf1e6',
  /** @deprecated Use `semanticColors.contentWarning` */
  warning400: '#e77309',
  /** @deprecated Use `semanticColors.backgroundSuccessSubtle` */
  success30: '#f7fbfa',
  /** @deprecated Use `semanticColors.backgroundSuccess` */
  success100: '#e5f2ed',
  /** @deprecated Use `semanticColors.contentSuccess` */
  success400: '#008050',
  /** @deprecated Use `semanticColors.backgroundErrorSubtle` */
  alert30: '#fef8f8',
  /** @deprecated Use `semanticColors.backgroundError` */
  alert100: '#fce9ea',
  /** @deprecated Use `semanticColors.contentError` */
  alert400: '#e4252c',
  /** @deprecated Use `semanticColors.contentInvert` or `semanticColors.backgroundSecondary` */
  white: '#ffffff',
  gray60: '#f8f6f2',
  gray70: '#f6f7f9',
  gray80: '#eceff3',
  /** @deprecated Use `semanticColors.borderDefault` */
  gray90: '#e9e8eB',
  /** @deprecated Use `semanticColors.contentDisabled` or `semanticColors.borderStrong` */
  gray100: '#b6b4b9',
  gray200: '#c6cedc',
  gray300: '#b3bdcf',
  gray400: '#a0adc3',
  gray500: '#8691a7',
  /** @deprecated Use `semanticColors.contentSecondary` */
  gray600: '#494550',
  gray700: '#505869',
  gray800: '#404858',
  gray900: '#323848',
  /** @deprecated Use `semanticColors.contentPrimary` */
  black: '#0e0f16',
  /** @deprecated Use `colorScale.primary50` from `./tokens` */
  purple100: '#F4EDFF',
  /** @deprecated Use `semanticColors.contentBrand` or `semanticColors.interactiveDefault` */
  purple400: '#8B4AFF',
  purple600: '#6a2bd6',
  heart: '#fe2c55',
  /** @deprecated Use `semanticColors.backgroundPrimary` */
  primarySurface: '#f5f5f6',
  /** @deprecated Use `semanticColors.contentBrand` */
  goodstack: '#8B4AFF',
  /** @deprecated Use `semanticColors.contentPrimary` */
  text_primary: '#0D0D0D',
  // Figma content/tertiary. Use this primitive (or `theme.colors.contentTertiary`);
  // `theme.semantic.contentTertiary` is still aliased to neutral300 (#6D6A73) until
  // the audit in NEX-600's follow-up consolidates the two.
  contentTertiary: '#615E67',
  // Figma content/disabled. `theme.semantic.contentDisabled` happens to alias
  // gray100 (same hex), so the audit is naming-hygiene only.
  contentDisabled: '#B6B4B9',
}
